// Vendor Spend Optimization Agent UI (Polished)
// Additions: Pareto chart for concentration, trend line chart, scorecard visualization,
// summary strip, action buttons for merge/negotiate workflows.
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Chip, Stack, Paper, Grid, Divider, IconButton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import HandshakeIcon from '@mui/icons-material/Handshake';
import AgentPageShell from './AgentPageShell';
import {
    SummaryGrid, SectionHeader, DrillTable, EmptyState, LoadingBlock,
    SimpleBarChart, DonutChart, RadialGauge, fmtMoney, CHART_COLORS,
} from './AgentUIKit';
import {
    vsConcentration, vsTerms, vsDuplicates, vsTrend, vsBenchmark, vsPerformance,
} from './api';

const TABS = ['Concentration (Pareto)', 'Contract Terms', 'Duplicates', 'Spend Trend', 'Benchmarks', 'Performance Scorecards'];

export default function VendorSpendAgent() {
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [conc, setConc] = useState(null);
    const [terms, setTerms] = useState(null);
    const [dupes, setDupes] = useState(null);
    const [trend, setTrend] = useState(null);
    const [bench, setBench] = useState(null);
    const [perf, setPerf] = useState(null);

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        const load = async () => {
            if (tab === 0 && !conc) { const r = await run(() => vsConcentration(365)); if (r?.data?.success) setConc(r.data.data); }
            else if (tab === 1 && !terms) { const r = await run(() => vsTerms()); if (r?.data?.success) setTerms(r.data.data); }
            else if (tab === 2 && !dupes) { const r = await run(() => vsDuplicates()); if (r?.data?.success) setDupes(r.data.data); }
            else if (tab === 3 && !trend) { const r = await run(() => vsTrend(12)); if (r?.data?.success) setTrend(r.data.data); }
            else if (tab === 4 && !bench) { const r = await run(() => vsBenchmark()); if (r?.data?.success) setBench(r.data.data); }
            else if (tab === 5 && !perf) { const r = await run(() => vsPerformance()); if (r?.data?.success) setPerf(r.data.data); }
        };
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const summaryCards = [
        { label: 'Total Spend (365d)', value: fmtMoney(conc?.total_spend), accent: 'primary' },
        { label: 'Active Vendors', value: conc?.vendor_count ?? '—', accent: 'info' },
        { label: 'At-Risk Concentration', value: conc?.at_risk_vendors ?? '—', accent: conc?.at_risk_vendors > 0 ? 'warning' : 'success' },
        { label: 'Potential Savings (terms)', value: fmtMoney(terms?.total_potential_savings), accent: 'success' },
    ];

    // Pareto data for concentration: top 10 vendors sorted by share, with cumulative %
    const paretoData = (() => {
        if (!conc?.vendors) return [];
        const sorted = [...conc.vendors].sort((a, b) => b.total_spend - a.total_spend).slice(0, 10);
        let cumulative = 0;
        return sorted.map(v => {
            cumulative += v.share_pct;
            return {
                vendor: v.vendor_name?.substring(0, 18),
                spend: v.total_spend,
                cumulative: Math.min(100, cumulative),
            };
        });
    })();

    const renderTab = () => {
        switch (tab) {
            case 0:
                return conc ? (
                    <Stack spacing={1.5}>
                        <SectionHeader
                            title="Vendor Concentration (Pareto)"
                            subtitle="80/20 rule — identify vendors with disproportionate share of spend"
                            actions={<IconButton size="small" onClick={() => { setConc(null); run(() => vsConcentration(365)).then(r => r?.data?.success && setConc(r.data.data)); }}><RefreshIcon fontSize="small" /></IconButton>}
                        />
                        {paretoData.length > 0 && (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600}>Top 10 Vendors by Spend</Typography>
                                <SimpleBarChart
                                    data={paretoData}
                                    xKey="vendor"
                                    series={[{ key: 'spend', label: 'Spend', color: CHART_COLORS.primary }]}
                                    height={280}
                                    yFormatter={v => v >= 1000 ? `$${(v/1000).toFixed(0)}K` : fmtMoney(v)}
                                    layout="vertical"
                                    showLegend={false}
                                />
                            </Paper>
                        )}
                        <DrillTable
                            columns={[
                                { key: 'vendor_name', label: 'Vendor' },
                                { key: 'total_spend', label: 'Total', align: 'right', render: r => fmtMoney(r.total_spend) },
                                { key: 'share_pct', label: 'Share %', align: 'right', render: r => `${r.share_pct}%` },
                                { key: 'risk', label: 'Risk', render: r => r.at_risk && <Chip size="small" color="warning" label="concentrated" /> },
                            ]}
                            rows={conc.vendors || []}
                            rowKeyField="vendor_id"
                            maxHeight={420}
                        />
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 1:
                return terms ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Contract Term Optimization" subtitle="Estimated savings from 2/10 Net 30 early-pay discounts" />
                        <SummaryGrid cards={[
                            { label: 'Opportunities', value: (terms.opportunities || []).length, accent: 'primary' },
                            { label: 'Total Potential Savings', value: fmtMoney(terms.total_potential_savings), accent: 'success' },
                        ]} cols={2} />
                        <DrillTable
                            columns={[
                                { key: 'vendor_name', label: 'Vendor' },
                                { key: 'annual_spend', label: 'Annual Spend', align: 'right', render: r => fmtMoney(r.annual_spend) },
                                { key: 'bill_count', label: 'Bills', align: 'right' },
                                { key: 'estimated_annual_savings_2_10_net_30', label: 'Savings', align: 'right', render: r => <b style={{ color: CHART_COLORS.success }}>{fmtMoney(r.estimated_annual_savings_2_10_net_30)}</b> },
                                { key: 'talking_point', label: 'Talking Point' },
                                { key: 'action', label: '', render: () => <Button size="small" variant="outlined" startIcon={<HandshakeIcon />}>Negotiate</Button> },
                            ]}
                            rows={terms.opportunities || []}
                            rowKeyField="vendor_id"
                            maxHeight={420}
                        />
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 2:
                return dupes ? (
                    <Stack spacing={1}>
                        <SectionHeader title="Duplicate Vendor Detector" subtitle={`${dupes.duplicate_count} suspected duplicate pairs`} />
                        {(dupes.items || []).length === 0 ? (
                            <EmptyState message="No duplicate vendors detected." />
                        ) : (
                            <DrillTable
                                columns={[
                                    { key: 'reason', label: 'Reason' },
                                    { key: 'vendor_a', label: 'Vendor A', render: r => `${r.vendors[0]?.name} (#${r.vendors[0]?.id})` },
                                    { key: 'vendor_b', label: 'Vendor B', render: r => `${r.vendors[1]?.name} (#${r.vendors[1]?.id})` },
                                    { key: 'action', label: '', render: () => <Button size="small" variant="outlined" startIcon={<MergeTypeIcon />}>Merge</Button> },
                                ]}
                                rows={dupes.items}
                            />
                        )}
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 3:
                return trend ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Spend Trend (MoM)" subtitle={trend.months_compared?.join(' vs ')} />
                        {(trend.trends || []).length > 0 && (
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600}>Top 10 Vendor MoM Changes</Typography>
                                <SimpleBarChart
                                    data={trend.trends.slice(0, 10).map(t => ({
                                        vendor: t.vendor_name?.substring(0, 18),
                                        change: t.change_dollar,
                                    }))}
                                    xKey="vendor"
                                    series={[{ key: 'change', label: '$ Change', color: CHART_COLORS.warning }]}
                                    height={280}
                                    yFormatter={v => fmtMoney(v)}
                                    layout="vertical"
                                    showLegend={false}
                                />
                            </Paper>
                        )}
                        <DrillTable
                            columns={[
                                { key: 'vendor_name', label: 'Vendor' },
                                { key: 'prior_month', label: 'Prior', align: 'right', render: r => fmtMoney(r.prior_month) },
                                { key: 'current_month', label: 'Current', align: 'right', render: r => fmtMoney(r.current_month) },
                                { key: 'change_dollar', label: '$ Change', align: 'right', render: r => fmtMoney(r.change_dollar) },
                                { key: 'change_pct', label: '% Change', align: 'right', render: r => `${r.change_pct}%` },
                                { key: 'direction', label: 'Dir', render: r => <Chip size="small" color={r.direction === 'INCREASE' ? 'warning' : 'info'} label={r.direction} /> },
                            ]}
                            rows={trend.trends || []}
                            rowKeyField="vendor_id"
                            maxHeight={420}
                        />
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 4:
                return bench ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Industry Benchmark Comparison" subtitle={`YTD revenue ${fmtMoney(bench.total_revenue_ytd)}`} />
                        <DrillTable
                            columns={[
                                { key: 'account_name', label: 'Account' },
                                { key: 'benchmark_label', label: 'Benchmark' },
                                { key: 'benchmark_pct_of_revenue', label: 'Bench %', align: 'right', render: r => `${r.benchmark_pct_of_revenue}%` },
                                { key: 'actual_pct_of_revenue', label: 'Actual %', align: 'right', render: r => `${r.actual_pct_of_revenue}%` },
                                { key: 'above_benchmark_pct_points', label: 'Gap', align: 'right', render: r => (
                                    <Chip size="small"
                                        color={r.above_benchmark_pct_points > 0 ? 'warning' : 'success'}
                                        label={`${r.above_benchmark_pct_points}pp`} />
                                )},
                                { key: 'potential_annual_savings', label: 'Savings Pot.', align: 'right', render: r => fmtMoney(r.potential_annual_savings) },
                            ]}
                            rows={bench.comparisons || []}
                            maxHeight={420}
                        />
                        {bench.note && <Typography variant="caption" color="text.secondary">{bench.note}</Typography>}
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 5:
                return perf ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Vendor Performance Scorecards" subtitle="Composite score based on on-time %, dispute rate, volume" />
                        {(perf.scorecards || []).length === 0 ? (
                            <EmptyState message="No scorecards yet." />
                        ) : (
                            <Grid container spacing={1}>
                                {perf.scorecards.slice(0, 8).map(sc => (
                                    <Grid key={sc.vendor_id} item xs={12} sm={6} md={3}>
                                        <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 13 }} noWrap>{sc.vendor_name}</Typography>
                                            <Box sx={{ mt: 1 }}>
                                                <RadialGauge
                                                    value={sc.score}
                                                    label={sc.score >= 80 ? 'Strong' : sc.score >= 60 ? 'OK' : 'Weak'}
                                                    size={100}
                                                />
                                            </Box>
                                            <Stack direction="row" spacing={0.5} justifyContent="center" sx={{ mt: 1 }}>
                                                <Chip size="small" variant="outlined" label={`${sc.bills} bills`} />
                                                <Chip size="small" variant="outlined" label={`${sc.on_time_rate_pct}%`} />
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                {fmtMoney(sc.total_spend)}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            default: return null;
        }
    };

    return (
        <AgentPageShell
            title="Vendor Spend Optimization Agent"
            subtitle="Agentic Financial Operations — concentration (Pareto), contract terms, duplicates, trends, benchmarks, scorecards."
            tabs={TABS}
            activeTab={tab}
            onTabChange={setTab}
            error={error}
        >
            <Stack spacing={1.5}>
                <SummaryGrid cards={summaryCards} />
                <Divider />
                {renderTab()}
            </Stack>
        </AgentPageShell>
    );
}
